import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import has from 'lodash/has';
import set from 'lodash/set';

import { EntityRequirement } from '@evidentid/tprm-portal-lib/models/entity-details/EntityRequirement.model';
import { EvaluationResult } from '@evidentid/tprm-portal-lib/models/entity-details/EvaluationResult.model';

/**
 * Collects the unique relative field paths (e.g. "details/endorsements/waiverOfSubrogation")
 * from all evaluation results that belong to the given coverage type.
 * usedFields format: "#/{coverageType}/{...relative path}"
 */
function extractUsedFieldPaths(
    evaluationResults: EvaluationResult[],
    coverageType: string,
): Set<string> {
    const prefix = `#/${coverageType}/`;
    return new Set<string>(
        evaluationResults
            .flatMap((result) => result.usedFields)
            .filter((field) => field.startsWith(prefix))
            .map((field) => field.slice(prefix.length)),
    );
}

/**
 * Returns a copy of `requirement data` with any fields from `fieldPaths` that are missing injected as null.
 * Paths where an intermediate node is null or not an object are silently skipped
 * to avoid altering unrelated parts of the coverage structure.
 * Returns the original object unchanged if no fields were missing.
 */
function injectMissingFieldsAsNull(
    reqData: EntityRequirement,
    fieldPaths: Set<string>,
): EntityRequirement {
    const coverageCopy = cloneDeep(reqData);
    for (const fieldPath of fieldPaths) {
        const segments = fieldPath.split('/');
        const parentSegments = segments.slice(0, -1);
        const parent = parentSegments.length > 0 ? get(coverageCopy, parentSegments) : coverageCopy;
        const parentIsTraversable = parent !== null && parent !== undefined && typeof parent === 'object';

        if (parentIsTraversable && !has(coverageCopy, segments)) {
            set(coverageCopy, segments, null);
        }
    }

    return coverageCopy;
}

/**
 * Returns a copy of `coverage` with all fields referenced in `evaluationResults`
 * (scoped to `coverageType`) that are absent from the coverage injected as null.
 * This allows the UI to surface non-compliant messages for fields that were never extracted.
 * Returns the original requirement data unchanged if nothing is missing.
 */
export function injectNonExtractedFields(
    reqData: EntityRequirement,
    evaluationResults: EvaluationResult[],
    coverageType: string,
): EntityRequirement {
    const fieldPaths = extractUsedFieldPaths(evaluationResults, coverageType);
    if (fieldPaths.size === 0) {
        return reqData;
    }
    return injectMissingFieldsAsNull(reqData, fieldPaths);
}
